import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import {
  Lock,
  LogOut,
  Package,
  DollarSign,
  CheckCircle2,
  Clock,
  Upload,
  RefreshCcw,
  Send,
  Settings,
  BookOpen,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS = 'drugawarenessusa.admin.token';

function authHeaders(token) {
  return { 'X-Admin-Token': token };
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(LS) || '');
  const [password, setPassword] = useState('');

  if (!token) {
    return <AdminLogin onLogin={(t) => { localStorage.setItem(LS, t); setToken(t); }} password={password} setPassword={setPassword} />;
  }
  return <AdminDashboard token={token} onLogout={() => { localStorage.removeItem(LS); setToken(''); }} />;
}

function AdminLogin({ onLogin, password, setPassword }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/ebook/admin/login`, { password });
      onLogin(data.token);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <SEO title="Admin" path="/admin" />
      <div className="min-h-[80vh] flex items-center bg-slate-50">
        <form onSubmit={submit} className="w-full max-w-md mx-auto px-4">
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-8">
            <div className="h-12 w-12 rounded-md bg-[#0b2545] text-amber-400 flex items-center justify-center mx-auto mb-4">
              <Lock className="h-6 w-6" />
            </div>
            <h1 className="font-serif text-2xl font-bold text-[#0b2545] text-center">Admin Login</h1>
            <p className="text-sm text-slate-600 text-center mt-1">Enter the admin password to continue.</p>
            <div className="mt-6">
              <Label htmlFor="pw">Password</Label>
              <Input
                id="pw"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Admin password"
                autoFocus
                className="mt-2"
              />
            </div>
            {err && <div className="mt-3 text-sm text-red-600">{err}</div>}
            <Button type="submit" disabled={loading || !password} className="w-full mt-5 bg-[#0b2545] hover:bg-[#123a6b] text-white">
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </div>
          <div className="text-center mt-4">
            <Link to="/" className="text-xs text-slate-500 hover:text-amber-600">Back to site</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function AdminDashboard({ token, onLogout }) {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState('orders');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const h = { headers: authHeaders(token) };
    Promise.all([
      axios.get(`${API}/ebook/admin/stats`, h),
      axios.get(`${API}/ebook/admin/orders`, h),
      axios.get(`${API}/ebook/admin/settings`, h),
    ])
      .then(([s, o, se]) => {
        setStats(s.data);
        setOrders(o.data.orders || []);
        setSettings(se.data);
      })
      .catch((e) => {
        if (e?.response?.status === 401) {
          toast({ title: 'Session expired', description: 'Please sign in again.' });
          onLogout();
        }
      });
  }, [token, refreshKey, onLogout]);

  const refresh = () => setRefreshKey((k) => k + 1);

  return (
    <div>
      <SEO title="Admin Dashboard" path="/admin" />
      <div className="min-h-screen bg-slate-50">
        {/* Admin header */}
        <header className="bg-[#0b2545] text-white border-b border-black/20">
          <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Lock className="h-5 w-5 text-amber-400" />
              <div className="font-serif font-bold text-lg">Admin Dashboard</div>
              <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 uppercase tracking-widest">DrugAwarenessUSA</span>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={refresh} variant="outline" size="sm" className="bg-transparent border-white/30 text-white hover:bg-white hover:text-[#0b2545]">
                <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
              </Button>
              <Button onClick={onLogout} variant="outline" size="sm" className="bg-transparent border-white/30 text-white hover:bg-white hover:text-[#0b2545]">
                <LogOut className="h-3.5 w-3.5 mr-1.5" /> Sign out
              </Button>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={<Package className="h-5 w-5" />} label="Total Orders" value={stats?.total_orders ?? '—'} />
            <StatCard icon={<CheckCircle2 className="h-5 w-5" />} label="Completed" value={stats?.completed_orders ?? '—'} color="text-emerald-600" />
            <StatCard icon={<Clock className="h-5 w-5" />} label="Pending" value={stats?.pending_orders ?? '—'} color="text-amber-600" />
            <StatCard icon={<DollarSign className="h-5 w-5" />} label="Revenue" value={stats ? `$${stats.revenue_usd.toFixed(2)}` : '—'} />
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 border-b border-slate-200 mb-6">
            {[
              { k: 'orders', label: 'Orders', icon: <Package className="h-4 w-4" /> },
              { k: 'settings', label: 'Settings', icon: <Settings className="h-4 w-4" /> },
              { k: 'file', label: 'Ebook File', icon: <BookOpen className="h-4 w-4" /> },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                  tab === t.k
                    ? 'border-amber-500 text-[#0b2545]'
                    : 'border-transparent text-slate-500 hover:text-[#0b2545]'
                }`}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {tab === 'orders' && <OrdersTable orders={orders} token={token} onChange={refresh} />}
          {tab === 'settings' && settings && (
            <SettingsForm settings={settings} token={token} onSaved={(s) => { setSettings(s); refresh(); }} />
          )}
          {tab === 'file' && <EbookFileSection token={token} onDone={refresh} />}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color = 'text-[#0b2545]' }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5">
      <div className="flex items-center gap-2 text-amber-600">
        {icon}
        <span className="text-xs uppercase tracking-widest font-semibold">{label}</span>
      </div>
      <div className={`font-serif text-3xl font-bold mt-2 ${color}`}>{value}</div>
    </div>
  );
}

function OrdersTable({ orders, token, onChange }) {
  const [sending, setSending] = useState(null);

  const resend = async (orderId) => {
    setSending(orderId);
    try {
      await axios.post(`${API}/ebook/admin/resend-email/${orderId}`, {}, { headers: authHeaders(token) });
      toast({ title: 'Email sent', description: 'Confirmation email resent to the buyer.' });
      onChange?.();
    } catch (e) {
      toast({ title: 'Could not send', description: e?.response?.data?.detail || 'Error' });
    } finally {
      setSending(null);
    }
  };

  if (!orders.length) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-12 text-center text-slate-500">
        No orders yet. Once customers purchase the ebook, they’ll appear here.
      </div>
    );
  }
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-widest">
            <tr>
              <th className="px-4 py-3 text-left">Order</th>
              <th className="px-4 py-3 text-left">Buyer</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3 text-left">Downloads</th>
              <th className="px-4 py-3 text-left">Created</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {orders.map((o) => (
              <tr key={o.order_id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-mono text-xs text-[#0b2545]">
                  {o.order_id.slice(0, 8).toUpperCase()}
                  {o.mode === 'sandbox' && <span className="ml-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1">TEST</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-800">{o.buyer_name || '(no name)'}</div>
                  <div className="text-xs text-slate-500">{o.buyer_email}</div>
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={o.status} />
                </td>
                <td className="px-4 py-3 text-right">${o.captured_amount || o.amount_usd || '0.00'}</td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {o.download_count || 0} / {o.max_downloads || 0}
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {o.created_at ? new Date(o.created_at).toLocaleString() : '—'}
                </td>
                <td className="px-4 py-3 text-right">
                  {o.status === 'COMPLETED' && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={sending === o.order_id}
                      onClick={() => resend(o.order_id)}
                      className="border-[#0b2545] text-[#0b2545] hover:bg-[#0b2545] hover:text-white"
                    >
                      <Send className="h-3.5 w-3.5 mr-1.5" />
                      {sending === o.order_id ? 'Sending…' : 'Resend email'}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    COMPLETED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    CREATED: 'bg-amber-50 text-amber-700 border-amber-200',
    FAILED: 'bg-red-50 text-red-700 border-red-200',
  };
  const cls = map[status] || 'bg-slate-50 text-slate-700 border-slate-200';
  return <span className={`inline-block text-[11px] uppercase tracking-widest font-bold border rounded px-2 py-0.5 ${cls}`}>{status || 'unknown'}</span>;
}

function SettingsForm({ settings, token, onSaved }) {
  const [form, setForm] = useState({
    price_usd: settings.price_usd,
    title: settings.title,
    author: settings.author,
    description: settings.description,
    cover_url: settings.cover_url,
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/ebook/admin/settings`, form, { headers: authHeaders(token) });
      toast({ title: 'Saved', description: 'Settings updated.' });
      onSaved(data);
    } catch (e) {
      toast({ title: 'Could not save', description: e?.response?.data?.detail || 'Error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-white rounded-lg border border-slate-200 p-6 md:p-8 max-w-2xl">
      <h2 className="font-serif text-xl font-bold text-[#0b2545] mb-5">Ebook Settings</h2>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="price">Price (USD)</Label>
            <Input id="price" value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="title">Title</Label>
            <Input id="title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" />
          </div>
        </div>
        <div>
          <Label htmlFor="author">Author</Label>
          <Input id="author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="cover">Cover image URL</Label>
          <Input id="cover" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} className="mt-1.5" />
          {form.cover_url && (
            <img src={form.cover_url} alt="cover preview" className="mt-3 h-32 rounded-md border border-slate-200" />
          )}
        </div>
        <div>
          <Label htmlFor="desc">Description</Label>
          <Textarea id="desc" rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" />
        </div>
      </div>
      <Button type="submit" disabled={saving} className="mt-6 bg-[#0b2545] hover:bg-[#123a6b] text-white">
        {saving ? 'Saving…' : 'Save settings'}
      </Button>
    </form>
  );
}

function EbookFileSection({ token, onDone }) {
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/ebook/admin/upload-ebook`, fd, {
        headers: { ...authHeaders(token), 'Content-Type': 'multipart/form-data' },
      });
      toast({ title: 'Uploaded', description: 'The ebook file has been replaced.' });
      setFile(null);
      onDone?.();
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || 'Error' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-6 md:p-8 max-w-2xl">
      <h2 className="font-serif text-xl font-bold text-[#0b2545] mb-2">Ebook file</h2>
      <p className="text-sm text-slate-600 mb-5">
        Upload a new EPUB to replace the current ebook. Max 50MB. The file is stored privately on the
        server and never exposed through a public URL.
      </p>
      <div className="p-5 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800 mb-5">
        Note: after a production redeploy, the server auto-restores the ebook from the configured
        backup URL. If you upload a new file in production, you may need to re-upload after any future
        redeploy, or ask the developer to update the backup URL.
      </div>
      <div className="flex flex-col sm:flex-row items-start gap-3">
        <input
          type="file"
          accept=".epub,application/epub+zip"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="block w-full text-sm text-slate-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#0b2545] file:text-white hover:file:bg-[#123a6b]"
        />
        <Button onClick={upload} disabled={!file || uploading} className="bg-amber-500 hover:bg-amber-400 text-[#0b2545] font-semibold">
          <Upload className="mr-2 h-4 w-4" /> {uploading ? 'Uploading…' : 'Replace file'}
        </Button>
      </div>
    </div>
  );
}
